/**
 * TrainingSlide - Slide 5
 * Training progress with epoch snapshots showing 4 preview images each
 */

import { useState } from "react";
import {
  AlertCircle,
  Clock,
  Cpu,
  Loader2,
  Check,
  X,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { CheckpointSampleThumbnail } from "@/components/loras/CheckpointSampleThumbnail";
import { CheckpointSelector } from "@/components/loras/CheckpointSelector";
import { CheckpointComparisonModal } from "@/components/loras/CheckpointComparisonModal";
import type { TrainingProgress } from "../types";
import type { Checkpoint, TrainingRunStatusSlug } from "@/types/loras";

interface TrainingSlideProps {
  isTraining: boolean;
  progress: TrainingProgress | null;
  onCancel: () => void | Promise<void>;
  onContinue: () => void;
  /** Real-mode signal: status from the live training run row. */
  realModeStatusSlug?: TrainingRunStatusSlug | undefined;
  /** Real-mode signal: error_message from the live training run row. */
  realModeErrorMessage?: string | null;
  /** Real-mode signal: cancel mutation in flight. */
  realModeCancelling?: boolean;
  /** Real-mode (Feature 2): checkpoints to choose the active one from. */
  realModeCheckpoints?: Checkpoint[];
  /** Real-mode: the LoRA's current default checkpoint id. */
  realModeDefaultCheckpointId?: string | null;
  /** Real-mode: persist the chosen checkpoint. */
  onSelectCheckpoint?: (checkpointId: string) => void;
  /** Real-mode: set-default mutation in flight. */
  realModeSettingDefault?: boolean;
  /** Real-mode: number of auto-resume attempts for this run. */
  realModeResumeAttemptCount?: number | null;
  /** Real-mode: if resumed run, the original run ID. */
  realModeResumedFromRunId?: string | null;
  /** Real-mode: checkpoint step this run resumed from. */
  realModeResumeFromCheckpointStep?: number | null;
  /** Real-mode: handler to retry training after a failure. */
  onRetry?: () => void | Promise<void>;
  /** Real-mode: retry mutation in flight. */
  realModeRetrying?: boolean;
  /** Real-mode: error message from retry attempt. */
  realModeRetryError?: string | null;
}

const REAL_MODE_STATUS_LABEL: Record<string, string> = {
  queued: "Queued for submission",
  submitting: "Submitting to AWS Batch",
  pending_capacity: "Waiting for GPU capacity",
  training: "Training in progress",
  uploading_artifacts: "Uploading final artifacts",
  completed: "Training complete",
  failed: "Training failed",
  failed_resumable: "Infrastructure failure — auto-resuming",
  resumed: "Resumed (see new run)",
  cancelled: "Training cancelled",
};

export function TrainingSlide({
  isTraining,
  progress,
  onCancel,
  onContinue,
  realModeStatusSlug,
  realModeErrorMessage,
  realModeCancelling,
  realModeCheckpoints,
  realModeDefaultCheckpointId,
  onSelectCheckpoint,
  realModeSettingDefault,
  realModeResumeAttemptCount,
  realModeResumedFromRunId,
  realModeResumeFromCheckpointStep,
  onRetry,
  realModeRetrying,
  realModeRetryError,
}: TrainingSlideProps) {
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const realMode = realModeStatusSlug !== undefined;
  const isFailedResumable =
    realMode && realModeStatusSlug === "failed_resumable";
  const isResumed = realMode && realModeStatusSlug === "resumed";
  const isResumedRun =
    realMode &&
    (realModeResumedFromRunId != null || (realModeResumeAttemptCount ?? 0) > 0);

  if (!progress) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-primary animate-spin" />
          <p className="text-muted-foreground">
            {realMode
              ? (REAL_MODE_STATUS_LABEL[realModeStatusSlug ?? ""] ??
                "Preparing training...")
              : "Preparing training..."}
          </p>
        </div>
      </div>
    );
  }

  const percentComplete = Math.round(
    progress.totalEpochs > 0
      ? (progress.currentEpoch / progress.totalEpochs) * 100
      : 0,
  );
  const isComplete = realMode
    ? realModeStatusSlug === "completed"
    : !isTraining && progress.currentEpoch === progress.totalEpochs;
  const isFailed = realMode && realModeStatusSlug === "failed";
  const isCancelled = realMode && realModeStatusSlug === "cancelled";

  const handleConfirmCancel = () => {
    setShowCancelDialog(false);
    void onCancel();
  };

  const headline = realMode
    ? isComplete
      ? "Training complete"
      : isFailed
        ? "Training failed"
        : isFailedResumable
          ? "Infrastructure failure"
          : isResumed
            ? "Run superseded"
            : isCancelled
              ? "Training cancelled"
              : (REAL_MODE_STATUS_LABEL[realModeStatusSlug ?? ""] ??
                "Training in progress")
    : isComplete
      ? "Training complete"
      : "Training in progress";

  const subline = realMode
    ? isComplete
      ? "Your LoRA model has finished training. Continue to lock the trigger word."
      : isFailed
        ? "AWS Batch reported a failure. See details below."
        : isFailedResumable
          ? "The training instance was interrupted. A new run is being created automatically from the last checkpoint."
          : isResumed
            ? "This run was automatically resumed in a new training run. Check the new run for current progress."
            : isCancelled
              ? "The training job was cancelled before completion."
              : "Tracking the live AWS Batch job. Checkpoints appear here as they're produced."
    : isComplete
      ? "Your LoRA model has finished training. Continue to test its performance."
      : "Your LoRA model is being trained. Preview images will appear at each snapshot interval.";

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-foreground mb-2">
          {headline}
        </h2>
        <p className="text-sm text-muted-foreground max-w-xl">{subline}</p>
      </div>

      {/* Real-mode failure banner */}
      {realMode && (isFailed || isCancelled) && realModeErrorMessage && (
        <div
          className="flex items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/5 p-4 mb-6"
          role="alert"
          data-testid="training-slide-error"
        >
          <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-xs text-red-400 font-medium">
              {isFailed ? "Failure detail" : "Cancellation detail"}
            </p>
            <p className="text-[11px] text-red-400/80 mt-1 font-mono">
              {realModeErrorMessage}
            </p>
          </div>
        </div>
      )}

      {/* Real-mode: auto-resume in progress banner */}
      {isFailedResumable && (
        <div
          className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 mb-6"
          role="status"
          data-testid="training-slide-auto-resume"
        >
          <Loader2 className="w-4 h-4 text-amber-400 shrink-0 mt-0.5 animate-spin" />
          <div>
            <p className="text-xs text-amber-400 font-medium">
              Auto-resuming from checkpoint
            </p>
            <p className="text-[11px] text-amber-400/80 mt-1">
              The training instance was interrupted (spot reclaim or hardware
              failure). A new training run is being created from step{" "}
              {realModeResumeFromCheckpointStep ?? "latest"}.
              {(realModeResumeAttemptCount ?? 0) > 0 && (
                <> This is resume attempt #{realModeResumeAttemptCount}.</>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Real-mode: this run was superseded (resumed status) */}
      {isResumed && (
        <div
          className="flex items-start gap-3 rounded-lg border border-blue-500/30 bg-blue-500/5 p-4 mb-6"
          role="status"
          data-testid="training-slide-resumed"
        >
          <AlertCircle className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-xs text-blue-400 font-medium">Run superseded</p>
            <p className="text-[11px] text-blue-400/80 mt-1">
              This run was automatically continued in a new training run after
              an infrastructure failure. Check the LoRA detail page for the
              current active run.
            </p>
          </div>
        </div>
      )}

      {/* Real-mode: this run is a resumed run (info banner) */}
      {isResumedRun && !isFailedResumable && !isResumed && (
        <div
          className="flex items-start gap-3 rounded-lg border border-blue-500/30 bg-blue-500/5 p-4 mb-6"
          role="status"
          data-testid="training-slide-resumed-run-info"
        >
          <AlertCircle className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-xs text-blue-400 font-medium">
              Resumed training run
            </p>
            <p className="text-[11px] text-blue-400/80 mt-1">
              This run continued from checkpoint step{" "}
              {realModeResumeFromCheckpointStep ?? "unknown"} of a previous run
              {(realModeResumeAttemptCount ?? 0) > 1 && (
                <> (resume attempt #{realModeResumeAttemptCount})</>
              )}
              .
            </p>
          </div>
        </div>
      )}

      {/* Retry button for failed runs */}
      {realMode && isFailed && onRetry && (
        <div className="mb-6" data-testid="training-slide-retry">
          <Button
            size="lg"
            className="w-full"
            onClick={() => void onRetry()}
            disabled={realModeRetrying}
          >
            {realModeRetrying ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Starting new training run…
              </>
            ) : (
              "Retry Training"
            )}
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-2">
            This will start a fresh training run with the same settings.
          </p>
          {realModeRetryError && (
            <p className="text-xs text-red-400 text-center mt-2">
              {realModeRetryError}
            </p>
          )}
        </div>
      )}

      {/* Training Stats Bar */}
      <div className="rounded border border-white/10 bg-[#1a1a1a] p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          {/* Left: Progress info */}
          <div className="flex items-center gap-6">
            {/* Overall progress */}
            <div className="flex items-center gap-2">
              {isTraining ? (
                <Loader2 className="w-4 h-4 text-primary animate-spin" />
              ) : (
                <Check className="w-4 h-4 text-green-500" />
              )}
              <span className="text-sm font-medium text-foreground">
                {percentComplete}%
              </span>
            </div>

            {/* Current step / epoch */}
            <div className="flex items-center gap-2">
              <Cpu className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {realMode ? "Step" : "Epoch"}{" "}
                <span className="text-foreground font-medium">
                  {progress.currentEpoch}
                </span>{" "}
                / {progress.totalEpochs}
              </span>
            </div>

            {/* Time remaining (or live status label in real mode) */}
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {isComplete ? (
                  <span className="text-green-500 font-medium">Complete</span>
                ) : realMode ? (
                  <span className="text-foreground font-medium">
                    {progress.timeRemaining}
                  </span>
                ) : (
                  <>
                    <span className="text-foreground font-medium">
                      {progress.timeRemaining}
                    </span>{" "}
                    remaining
                  </>
                )}
              </span>
            </div>
          </div>

          {/* Right: Cancel button */}
          {isTraining && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setShowCancelDialog(true)}
              disabled={Boolean(realModeCancelling)}
            >
              {realModeCancelling ? (
                <>
                  <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                  Cancelling…
                </>
              ) : (
                "Cancel Training"
              )}
            </Button>
          )}
        </div>

        {/* Progress bar */}
        <Progress value={percentComplete} className="h-2" />
      </div>

      {/* Epoch / Checkpoint Snapshots */}
      <div className="space-y-4">
        <h3 className="text-xs text-muted-foreground uppercase tracking-wider">
          {realMode ? "Registered checkpoints" : "Epoch snapshots"}
        </h3>

        {progress.snapshots.length === 0 && isTraining && (
          <div className="rounded border border-white/10 bg-[#1a1a1a] p-8 text-center">
            <Loader2 className="w-6 h-6 text-primary animate-spin mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              {realMode
                ? "First checkpoint will appear once the trainer writes a .safetensors to S3 (typically after ~30–60 minutes of training)."
                : `First snapshot will appear at epoch ${progress.snapshotInterval}...`}
            </p>
          </div>
        )}

        {/* Snapshot grid - most recent first */}
        <div className="space-y-4 max-h-[400px] overflow-y-auto">
          {[...progress.snapshots].reverse().map((snapshot) => (
            <div
              key={snapshot.epoch}
              className="rounded border border-white/10 bg-[#1a1a1a] p-4"
            >
              {/* Snapshot header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-green-500/20">
                    <Check className="w-3 h-3 text-green-500" />
                  </div>
                  <span className="text-sm font-medium text-foreground">
                    {realMode ? "Step" : "Epoch"} {snapshot.epoch}
                  </span>
                </div>
                {!realMode && (
                  <span className="text-xs text-muted-foreground font-mono">
                    loss: {snapshot.loss.toFixed(4)}
                  </span>
                )}
              </div>

              {/* Preview images (mock mode only — v1 trainer has no auto-samples) */}
              {snapshot.previewImages.length > 0 && (
                <div className="grid grid-cols-6 gap-2">
                  {snapshot.previewImages.map((img, idx) => (
                    <div
                      key={idx}
                      className="aspect-square rounded bg-white/5 overflow-hidden relative transition-transform duration-200 ease-out origin-center hover:scale-[1.75] hover:z-30 hover:shadow-2xl hover:shadow-black/60"
                    >
                      <img
                        src={img}
                        alt={`${realMode ? "Step" : "Epoch"} ${snapshot.epoch} preview ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
              {realMode &&
                snapshot.previewImages.length === 0 &&
                (snapshot.sampleAssetIds &&
                snapshot.sampleAssetIds.length > 0 ? (
                  <div className="grid grid-cols-6 gap-2">
                    {snapshot.sampleAssetIds.slice(0, 4).map((assetId, idx) => (
                      <div
                        key={assetId}
                        className="aspect-square rounded bg-white/5 overflow-hidden relative transition-transform duration-200 ease-out origin-center hover:scale-[1.75] hover:z-30 hover:shadow-2xl hover:shadow-black/60"
                      >
                        <CheckpointSampleThumbnail
                          assetId={assetId}
                          alt={`Step ${snapshot.epoch} sample ${idx + 1}`}
                          className="w-full h-full"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[11px] text-muted-foreground">
                    Checkpoint registered to S3. Sample previews appear here as
                    the trainer emits them.
                  </p>
                ))}
            </div>
          ))}
        </div>

        {/* Currently training indicator */}
        {isTraining && progress.currentEpoch > 0 && (
          <div className="rounded border border-primary/30 bg-primary/5 p-4">
            <div className="flex items-center gap-3">
              <Loader2 className="w-4 h-4 text-primary animate-spin" />
              <span className="text-sm text-primary">
                {realMode
                  ? `At step ${progress.currentEpoch} of ${progress.totalEpochs}…`
                  : `Training epoch ${progress.currentEpoch}...`}
              </span>
              {!realMode && progress.snapshotInterval && (
                <span className="text-xs text-muted-foreground">
                  Next snapshot at epoch{" "}
                  {Math.ceil(
                    progress.currentEpoch / progress.snapshotInterval,
                  ) * progress.snapshotInterval}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Checkpoint selection (Feature 2) — pick the active checkpoint. */}
      {/* Enhanced visual prominence: sticky styling with distinct background */}
      {isComplete &&
        realMode &&
        realModeCheckpoints &&
        realModeCheckpoints.length > 0 && (
          <div className="mt-6 rounded-lg border-2 border-primary/30 bg-primary/5 p-5 sticky bottom-4 shadow-lg">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-semibold text-foreground">
                  Choose Your Checkpoint
                </h3>
              </div>
              {/* Compare All button to open full-screen modal */}
              <CheckpointComparisonModal
                checkpoints={realModeCheckpoints}
                defaultCheckpointId={realModeDefaultCheckpointId}
                onSelect={(id) => onSelectCheckpoint?.(id)}
                settingDefault={realModeSettingDefault}
              />
            </div>
            <p className="mb-4 text-[12px] text-muted-foreground">
              Select which training checkpoint to use for all future
              generations. Higher steps generally produce better results.
            </p>
            <CheckpointSelector
              checkpoints={realModeCheckpoints}
              defaultCheckpointId={realModeDefaultCheckpointId}
              onSelect={(id) => onSelectCheckpoint?.(id)}
              settingDefault={realModeSettingDefault}
            />
          </div>
        )}

      {/* Continue button when complete */}
      {isComplete && (
        <div className="mt-6">
          <Button size="lg" className="w-full" onClick={onContinue}>
            {realMode ? "Continue to lock" : "Continue to Testing"}
          </Button>
        </div>
      )}

      {/* Warning text when training */}
      {isTraining && (
        <p className="text-xs text-muted-foreground text-center mt-6">
          Please do not close this page while training is in progress.
        </p>
      )}

      {/* Cancel confirmation dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="sm:max-w-[425px] p-0 gap-0" hideCloseButton>
          {/* Header */}
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <div className="flex items-center justify-between">
              <DialogTitle>Cancel training?</DialogTitle>
              {/* Close Button */}
              <button
                type="button"
                onClick={() => setShowCancelDialog(false)}
                aria-label="Close modal"
                className="inline-flex items-center justify-center flex-shrink-0"
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "8px",
                  transition: "all 0.15s ease",
                  background: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.12)",
                  color: "#999",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)";
                  e.currentTarget.style.borderColor =
                    "rgba(255, 255, 255, 0.2)";
                  e.currentTarget.style.color = "#fff";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background =
                    "rgba(255, 255, 255, 0.05)";
                  e.currentTarget.style.borderColor =
                    "rgba(255, 255, 255, 0.12)";
                  e.currentTarget.style.color = "#999";
                }}
              >
                <X style={{ width: "18px", height: "18px" }} />
              </button>
            </div>
          </DialogHeader>

          {/* Content */}
          <div className="px-6 py-5">
            <DialogDescription>
              This will stop the training immediately. All progress will be lost
              and you will not be refunded for GPU time used.
            </DialogDescription>
          </div>

          {/* Footer */}
          <DialogFooter className="px-6 pt-4 pb-6 border-t">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowCancelDialog(false)}
            >
              Keep Training
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleConfirmCancel}
            >
              Yes, Cancel Training
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
